import clsx from 'clsx';
import React from 'react';

/**
 * Returns a createElement() type based on the props of the Component.
 * Useful for calculating what type a component should render as.
 *
 * @param props - A ReactElement props object
 * @returns A ReactElement type
 */
export function getElementType<P extends Record<string, any>>(props: P): React.ElementType {
  // ----------------------------------------
  // use defaultProp or 'div'
  return props.as || 'div';
}

// TYPE OF STYLES
type ITextElement = 'label' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

type ITextStyles = Record<ITextElement, string>;

const TEXT_STYLES: ITextStyles = {
  label: 'whitespace-nowrap overflow-hidden text-ellipsis font-light font-base',
  p: '',
  h1: 'tw-text-h1',
  h2: 'tw-text-h2',
  h3: 'tw-text-h3',
  h4: 'tw-text-h4',
  h5: 'tw-text-h5',
  h6: 'tw-text-h6',
};

const BASE_TEXT_STYLE = 'tw-underline-offset-2 tw-pointer-events-none';

type ITextChildren = {
  children: React.ReactNode;
};

export interface TextProps {
  as?: keyof JSX.IntrinsicElements | React.ComponentType;
  id?: string;
  className?: string;
  style?: any;
  ellipsis?: boolean;
  error?: boolean;
}
const Text = React.forwardRef<HTMLParagraphElement, TextProps & ITextChildren>(
  ({ as = 'p', error, ellipsis, className, ...props }, ref) => {
    const TextComponent = getElementType({ as });

    return (
      <TextComponent
        ref={ref}
        className={clsx(
          BASE_TEXT_STYLE,
          TEXT_STYLES[as as ITextElement],
          ellipsis && 'tw-text-ellipsis tw-overflow-hidden tw-whitespace-nowrap',
          error && 'tw-text-red',
          'tw-font-sans',
          className,
        )}
        {...props}
      />
    );
  },
);

Text.displayName = 'Text';
export default Text;
